import type { WebStudioBootSnapshot, WebStudioScope } from "./boot";
import { scopeToTarget } from "./boot";
import type {
  WebStudioActor,
  WebStudioCloudApiClient,
  WebStudioDiagnostic,
  WebStudioPresentationRow,
  WebStudioSignInAction,
  WebStudioSignInAvailability,
} from "./generatedClient";
import { makeWebStudioRequest } from "./transport";

/**
 * Account-session (auth gate) family adapter for the `web-studio-auth-gate`
 * change, over the generated `authenticate_start` / `authenticate` /
 * `sign_out` operations. The backend owns sign-in availability,
 * verification-code issuance and validation, session establishment
 * (browser-inaccessible cookies on the management origin), and server-side
 * revocation; the browser renders typed outcomes verbatim and never sees
 * raw tokens, code-validity logic, or session material.
 */

export const WEB_STUDIO_BROWSER_ACTOR: WebStudioActor = {
  actorKind: "browser_client",
  actorId: "web-studio-browser",
};

/**
 * Backend-declared sign-in availability plus the frontend-synthetic
 * `operations_absent` state for backends that predate the sign-in
 * operations (their boot payload carries no `signIn` block at all).
 */
export type WebStudioSignInAvailabilityState =
  | WebStudioSignInAvailability["availability"]
  | "operations_absent";

export interface WebStudioSignInAvailabilityView {
  availability: WebStudioSignInAvailabilityState;
  actions: WebStudioSignInAction[];
  diagnosticCode: string | null;
  guidance: string | null;
}

export type WebStudioSignInStartOutcome =
  | { kind: "code_sent"; email: string; provider: string; expiresIn: number | null }
  | { kind: "refused"; diagnostic: WebStudioDiagnostic };

export type WebStudioSignInCompleteOutcome =
  | { kind: "authenticated"; provider: string }
  | { kind: "refused"; diagnostic: WebStudioDiagnostic };

export type WebStudioSignOutOutcome =
  | { kind: "signed_out"; logoutUrl: string | null }
  | { kind: "failed"; diagnostic: WebStudioDiagnostic };

const FALLBACK_DIAGNOSTIC: WebStudioDiagnostic = {
  code: "web_studio.auth.untyped_outcome",
  category: "authorization",
  severity: "error",
  retryability: "retryable",
  message: "The backend response did not carry a typed sign-in outcome.",
  correlationId: "browser:auth-gate:untyped",
  redactionState: "public_metadata_only",
};

/**
 * Reads the backend-owned `signIn` block from a signed-out boot payload. A
 * missing block means the backend predates the sign-in operations, which the
 * gate renders as typed unavailability guidance — never a fabricated form.
 */
export function readWebStudioSignInAvailability(
  boot: WebStudioBootSnapshot,
): WebStudioSignInAvailabilityView {
  const block = boot.data.signIn;
  if (block === undefined) {
    return {
      availability: "operations_absent",
      actions: [],
      diagnosticCode: null,
      guidance: null,
    };
  }
  return {
    availability: block.availability,
    actions: block.availability === "available" ? block.actions : [],
    diagnosticCode: block.diagnosticCode,
    guidance: block.guidance,
  };
}

/**
 * When the backend labels the boot's account presentation as a fixture
 * session (`local-studio-user` / `local_auth_fixture` source), returns the
 * backend-provided label so account-presenting chrome can render it.
 */
export function readWebStudioFixtureSessionLabel(boot: WebStudioBootSnapshot): string | null {
  return fixtureSessionLabelFromRows(boot.data.accountSessionRows ?? []);
}

export function fixtureSessionLabelFromRows(
  rows: readonly WebStudioPresentationRow[],
): string | null {
  const fixtureSourceRow = rows.find(
    (row) => row.label === "local_auth_fixture" || row.status === "local_auth_fixture",
  );
  if (fixtureSourceRow === undefined) {
    return null;
  }
  const labelRow = rows.find((row) => row.label === "local-studio-user");
  return labelRow?.label ?? fixtureSourceRow.label;
}

export interface WebStudioSignInStartInput {
  email: string;
  inviteCode?: string | undefined;
}

export async function startWebStudioSignIn(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  input: WebStudioSignInStartInput,
): Promise<WebStudioSignInStartOutcome> {
  const response = await client.authenticateStart(
    makeWebStudioRequest("authenticateStart", scopeToTarget(scope), {
      actor: WEB_STUDIO_BROWSER_ACTOR,
      body: {
        email: input.email,
        ...(input.inviteCode !== undefined ? { invite_code: input.inviteCode } : {}),
      },
    }),
  );
  const start = response.data.signInStart;
  if (start?.status === "code_sent") {
    return {
      kind: "code_sent",
      email: start.email || input.email,
      provider: start.provider,
      expiresIn: typeof start.expiresIn === "number" ? start.expiresIn : null,
    };
  }
  return { kind: "refused", diagnostic: primaryDiagnostic(response.diagnostics) };
}

export interface WebStudioSignInCompleteInput {
  email: string;
  code: string;
  inviteCode?: string | undefined;
}

/**
 * Completes the code sign-in through the generated `authenticate` operation.
 * On success the backend response establishes the browser-inaccessible
 * session cookies on the management origin; the JSON body carries only the
 * typed `signInComplete` confirmation.
 */
export async function completeWebStudioSignIn(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  input: WebStudioSignInCompleteInput,
): Promise<WebStudioSignInCompleteOutcome> {
  const response = await client.authenticate(
    makeWebStudioRequest("authenticate", scopeToTarget(scope), {
      actor: WEB_STUDIO_BROWSER_ACTOR,
      body: {
        email: input.email,
        code: input.code,
        ...(input.inviteCode !== undefined ? { invite_code: input.inviteCode } : {}),
      },
    }),
  );
  const complete = response.data.signInComplete;
  if (complete?.status === "authenticated") {
    return { kind: "authenticated", provider: complete.provider };
  }
  return { kind: "refused", diagnostic: primaryDiagnostic(response.diagnostics) };
}

export async function signOutWebStudio(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
): Promise<WebStudioSignOutOutcome> {
  const response = await client.signOut(
    makeWebStudioRequest("signOut", scopeToTarget(scope), {
      actor: WEB_STUDIO_BROWSER_ACTOR,
      body: {},
    }),
  );
  const signOut = response.data.signOut;
  // Sign-out is only real when the backend confirmed the server-side
  // revocation; anything else renders as a typed failure and the app keeps
  // treating the session as live.
  if (signOut?.status === "signed_out" && signOut.revoked === true) {
    return { kind: "signed_out", logoutUrl: signOut.logoutUrl ?? null };
  }
  return { kind: "failed", diagnostic: primaryDiagnostic(response.diagnostics) };
}

function primaryDiagnostic(
  diagnostics: ReadonlyArray<{ diagnostic: WebStudioDiagnostic }>,
): WebStudioDiagnostic {
  return diagnostics[0]?.diagnostic ?? FALLBACK_DIAGNOSTIC;
}
